interface EmailResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

export class EmailService {
  private static async callEmailFunction(
    projectId: string,
    emailType: 'lead' | 'confirmation'
  ): Promise<EmailResponse> {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-project-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          projectId,
          emailType,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`Email service error: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      return {
        success: true,
        messageId: result.messageId,
      };
    } catch (error) {
      console.error('Email sending failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Send lead generation email to admin
   * @param projectId - The project ID to send lead email for
   * @returns Promise<EmailResponse>
   */
  static async sendProjectLeadEmail(projectId: string): Promise<EmailResponse> {
    console.log(`Sending lead email for project: ${projectId}`);
    return this.callEmailFunction(projectId, 'lead');
  }

  /**
   * Send confirmation email to customer
   * @param projectId - The project ID to send confirmation email for
   * @returns Promise<EmailResponse>
   */
  static async sendCustomerConfirmationEmail(projectId: string): Promise<EmailResponse> {
    console.log(`Sending confirmation email for project: ${projectId}`);
    return this.callEmailFunction(projectId, 'confirmation');
  }

  /**
   * Send both lead and confirmation emails
   * @param projectId - The project ID to send emails for
   * @returns Promise<{lead: EmailResponse, confirmation: EmailResponse}>
   */
  static async sendAllEmails(projectId: string): Promise<{
    lead: EmailResponse;
    confirmation: EmailResponse;
  }> {
    console.log(`Sending all emails for project: ${projectId}`);
    
    const [leadResult, confirmationResult] = await Promise.allSettled([
      this.sendProjectLeadEmail(projectId),
      this.sendCustomerConfirmationEmail(projectId),
    ]);

    return {
      lead: leadResult.status === 'fulfilled' ? leadResult.value : { success: false, error: 'Lead email failed' },
      confirmation: confirmationResult.status === 'fulfilled' ? confirmationResult.value : { success: false, error: 'Confirmation email failed' },
    };
  }
}
